import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Group,
  AdminPanelSettings,
  Person,
  Email,
} from '@mui/icons-material';
import { teamAPI } from '../services/api';

// Utility for colored console logs
const termcolor = (message: string, color: string) => message;

interface TeamMember {
  id: number;
  user_id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  is_active: boolean;
  joined_date: string;
  can_manage_templates: boolean;
  can_manage_clients: boolean;
  is_consultant_role: boolean;
  email_verified: boolean;
  last_sign_in: string | null;
}

interface TeamData {
  consultant_company: {
    id: number;
    name: string;
  };
  team_members: TeamMember[];
  total_members: number;
}

const TeamManagement: React.FC = () => {
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'network_member',
  });

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await teamAPI.getTeamMembers();

      console.log(termcolor('✅ Team data loaded successfully', 'green'));
      setTeamData(data);
    } catch (error: any) {
      console.error(termcolor('❌ Error loading team data:', 'red'), error);
      setError(error.response?.data?.error || 'Failed to load team data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'network_member',
    });
    setOpenDialog(true);
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      email: member.email,
      first_name: member.first_name,
      last_name: member.last_name,
      role: member.role,
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (editingMember) {
        // Update existing member (only role can be changed)
        await teamAPI.updateTeamMember(editingMember.id, {
          role: formData.role,
        });
        console.log(termcolor('✅ Team member updated successfully', 'green'));
      } else {
        // Add new member
        await teamAPI.addTeamMember(formData);
        console.log(termcolor('✅ Team member added successfully', 'green'));
      }

      setOpenDialog(false);
      await loadTeamData(); // Reload data
    } catch (error: any) {
      console.error(termcolor('❌ Error saving team member:', 'red'), error);
      setError(error.response?.data?.error || 'Failed to save team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMember = async (member: TeamMember) => {
    if (!window.confirm(`Are you sure you want to remove ${member.first_name} ${member.last_name} from the team?`)) {
      return;
    }

    try {
      await teamAPI.removeTeamMember(member.id);
      
      console.log(termcolor('✅ Team member removed successfully', 'green'));
      await loadTeamData(); // Reload data
    } catch (error: any) {
      console.error(termcolor('❌ Error removing team member:', 'red'), error);
      setError(error.response?.data?.error || 'Failed to remove team member');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
      case 'network_admin':
        return <AdminPanelSettings />;
      case 'network_member':
        return <Person />;
      default:
        return <Group />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'network_admin':
        return 'primary';
      case 'network_member':
        return 'info';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading team data...
        </Typography>
      </Container>
    );
  }

  if (!teamData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load team data. You may not have permission to manage team members.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Team Members
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your consulting team for {teamData.consultant_company.name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddMember}
          size="large"
        >
          Add Team Member
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Team Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Group color="primary" />
            <Typography variant="h6">
              {teamData.total_members} Team Members
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Network Admins can manage clients and templates. Network Members can conduct audits.
          </Typography>
        </CardContent>
      </Card>

      {/* Team Members Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamData.team_members.map((member) => (
              <TableRow key={member.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getRoleIcon(member.role)}
                    <Typography variant="body2">
                      {member.first_name} {member.last_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">{member.email}</Typography>
                    {!member.email_verified && (
                      <Chip 
                        label="Pending Verification" 
                        size="small" 
                        color="warning"
                        icon={<Email />}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.role_display}
                    color={getRoleColor(member.role) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {member.can_manage_clients && (
                      <Chip label="Clients" size="small" variant="outlined" />
                    )}
                    {member.can_manage_templates && (
                      <Chip label="Templates" size="small" variant="outlined" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(member.joined_date).toLocaleDateString()}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit Role">
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditMember(member)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from Team">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteMember(member)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Team Member' : 'Add Team Member'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editingMember && (
              <>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    fullWidth
                  />
                </Box>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  fullWidth
                  helperText="An invitation email will be sent to this address"
                />
                <Alert severity="info">
                  The team member will receive an email invitation to set up their account and choose their own password.
                </Alert>
              </>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="network_admin">Network Admin</MenuItem>
                <MenuItem value="network_member">Network Member</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Role Permissions:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Network Admin:</strong> Can manage client companies, templates, and team members
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Network Member:</strong> Can conduct audits and access client data
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={isSubmitting || (!editingMember && (!formData.email || !formData.first_name || !formData.last_name))}
          >
            {isSubmitting ? <CircularProgress size={20} /> : (editingMember ? 'Update' : 'Send Invitation')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamManagement; 